import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { ScrapedAd } from './types';

// The merged Fetched Ads table is driven entirely by this registry: each
// pickable column is a ColumnDef with a render fn. The checkbox, expand-chevron
// and Actions cells are fixed in AdBrowser and are NOT part of this list.

export type AdRow = ScrapedAd;

export interface FeatureDist { value: string; count: number; pct: number; }

/** Render-time context passed to every column render fn. */
export interface ColumnCtx {
  /** Per-loaded-rows value distribution, keyed by feature column id. */
  featureDist: Record<string, FeatureDist[]>;
  processingAdIds: Set<string>;
  /** ad_id → storyboard usage count, populated from /storyboards/template-usage.
   *  Empty / missing entries render as 0 in the scriptsCount column. */
  templateUsage?: Map<string, number>;
  /** Click handler for the scriptsCount column. AdBrowser navigates to the
   *  Scripts tab with a templateAdId query param pre-set. */
  onScriptsClick?: (adId: string) => void;
}

export type ColumnGroup = 'Ad' | 'Pipeline' | 'Video Creative';

export interface ColumnDef {
  id: string;
  label: string;
  group: ColumnGroup;
  align?: 'left' | 'center' | 'right';
  sortable?: boolean;
  /** Field used by useSortableTable; defaults to `id`. */
  sortKey?: string;
  /** Explanatory text shown on hovering the column header. */
  headerTooltip?: string;
  render: (ad: AdRow, ctx: ColumnCtx) => React.ReactNode;
}

// Sentinel bucket for rows with no extracted value — still counted toward the
// loaded-rows denominator in a column's breakdown.
export const DIST_NONE = '__none__';

// Feature columns that get a per-loaded-rows breakdown tooltip + prevalence
// coloring. Each id is also the field name on the ad row.
export const FEATURE_COLUMN_FIELDS = [
  'awarenessLevel', 'hookDevice', 'persuasionAngle', 'proofType',
  'narrativeStructure', 'primaryEmotion', 'presenter', 'productionFormat',
  'tone', 'offerType', 'ctaType',
];

// Display-label overrides for enum values whose default title-casing reads
// wrong (e.g. "Voiceover Broll" → "Voiceover B-Roll").
const LABEL_OVERRIDES: Record<string, string> = {
  voiceover_broll: 'Voiceover B-Roll',
};

export function formatEnumLabel(val: string | null | undefined): string {
  if (!val) return '-';
  if (LABEL_OVERRIDES[val]) return LABEL_OVERRIDES[val];
  return val.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// Prevalence palette mirrors ProductMapping: dominant (>40%) green, common
// (>20%) blue, rarer values medium gray.
export function prevalenceColor(pct: number | undefined): string {
  if (pct == null) return 'inherit';
  if (pct > 40) return '#2e7d32';
  if (pct > 20) return '#1976d2';
  return '#757575';
}

/** Pipeline state rank — drives both the status chip and column sorting. */
export function pipelineRank(ad: AdRow): number {
  const t = ad.transcriptionStatus === 'DONE';
  const e = ad.extractionStatus === 'DONE';
  const anyFailed = ad.transcriptionStatus === 'FAILED' || ad.extractionStatus === 'FAILED';
  const anyStarted = !!ad.transcriptionStatus || !!ad.extractionStatus;
  if (t && e) return 0;
  if (anyStarted && !anyFailed) return 1;
  if (!anyStarted) return 2;
  return 3;
}

/** Distribution of one field's values over the loaded rows. The `None`
 *  bucket is still shown for context (count only), but the % column is
 *  calculated over rows that *have* a value — the denominator excludes
 *  None. This makes "Hook Device: cliffhanger 33%" mean "33% of extracted
 *  ads use cliffhanger" rather than "33% of loaded rows", which conflated
 *  un-extracted ads with extracted-but-no-cliffhanger ads. */
export function computeFeatureDist(ads: AdRow[], field: string): FeatureDist[] {
  const counts: Record<string, number> = {};
  for (const ad of ads) {
    const raw = (ad as any)[field];
    const key = raw ? String(raw) : DIST_NONE;
    counts[key] = (counts[key] || 0) + 1;
  }
  const extractedTotal = Object.entries(counts)
    .filter(([k]) => k !== DIST_NONE)
    .reduce((acc, [, c]) => acc + c, 0) || 1;
  return Object.entries(counts)
    .map(([value, count]) => ({
      value,
      count,
      pct: value === DIST_NONE ? 0 : Math.round((count / extractedTotal) * 100),
    }))
    .sort((a, b) => {
      if (a.value === DIST_NONE) return 1;
      if (b.value === DIST_NONE) return -1;
      return b.count - a.count;
    });
}

/** All feature-column distributions for the current page of rows. */
export function computeAllFeatureDists(ads: AdRow[]): Record<string, FeatureDist[]> {
  const out: Record<string, FeatureDist[]> = {};
  for (const f of FEATURE_COLUMN_FIELDS) out[f] = computeFeatureDist(ads, f);
  return out;
}

const STATUS_COLORS: Record<string, 'default' | 'warning' | 'info' | 'success' | 'error'> = {
  QUEUED: 'warning', PENDING: 'warning', PROCESSING: 'info', DONE: 'success', FAILED: 'error',
};

/** Ellipsised text with the full value on hover. */
function truncated(text: string | null | undefined, maxWidth = 200): React.ReactNode {
  const val = (text ?? '').toString();
  if (!val) return '-';
  return (
    <Tooltip title={val} arrow>
      <Box component="span" sx={{ display: 'block', maxWidth, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {val}
      </Box>
    </Tooltip>
  );
}

function statusChip(status: string | null | undefined): React.ReactNode {
  if (!status) return '-';
  return <Chip label={status} size="small" color={STATUS_COLORS[status] || 'default'} />;
}

/** Breakdown tooltip body — one color-dotted row per value. Percentages
 *  are over EXTRACTED ads (the None row is shown for context but is
 *  excluded from the denominator). */
function FeatureBreakdown({ label, dist }: { label: string; dist: FeatureDist[] }) {
  const loaded = dist.reduce((n, d) => n + d.count, 0);
  const extracted = dist.filter(d => d.value !== DIST_NONE).reduce((n, d) => n + d.count, 0);
  return (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5, color: '#fff' }}>
        {label} — {extracted}/{loaded} extracted
      </Typography>
      {dist.map(d => {
        const isNone = d.value === DIST_NONE;
        return (
          <Box key={d.value} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
            <Box sx={{
              width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
              bgcolor: isNone ? 'transparent' : d.pct > 40 ? '#2e7d32' : d.pct > 20 ? '#1976d2' : '#bdbdbd',
              border: isNone ? '1px solid #bdbdbd' : 'none',
            }} />
            <Typography variant="caption" sx={{ color: '#fff' }}>
              {isNone
                ? `None: ${d.count}`
                : `${formatEnumLabel(d.value)}: ${d.pct}% (${d.count})`}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

/** Feature column factory — prevalence-colored value with a breakdown tooltip
 *  (distribution over the loaded rows) shown on hovering the cell text. */
function featureColumn(id: string, label: string): ColumnDef {
  return {
    id, label, group: 'Video Creative', sortable: true,
    render: (ad, ctx) => {
      const raw = (ad as any)[id];
      const value = raw ? String(raw) : null;
      const dist = ctx.featureDist[id] || [];
      const pct = value ? dist.find(d => d.value === value)?.pct : undefined;
      return (
        <Tooltip title={<FeatureBreakdown label={label} dist={dist} />} arrow>
          <Box component="span" sx={{
            cursor: 'help',
            fontWeight: value ? 600 : 400,
            color: value ? prevalenceColor(pct) : 'text.disabled',
          }}>
            {value ? formatEnumLabel(value) : '-'}
          </Box>
        </Tooltip>
      );
    },
  };
}

export const ALL_COLUMNS: ColumnDef[] = [
  // ── Ad ──
  { id: 'pageName', label: 'Page', group: 'Ad', sortable: true,
    render: (ad) => truncated(ad.pageName, 180) },
  { id: 'headline', label: 'Headline', group: 'Ad', sortable: true,
    render: (ad) => truncated(ad.headline || ad.caption?.slice(0, 60), 240) },
  { id: 'hook', label: 'Hook', group: 'Ad', sortable: true, sortKey: 'hookText',
    headerTooltip: "The actual opening line(s) of the ad, taken verbatim from the transcript's hook segment (falls back to the headline if not yet extracted).",
    render: (ad) => truncated(ad.hookText || ad.headline, 260) },
  { id: 'keyword', label: 'Keyword', group: 'Ad', sortable: true,
    render: (ad) => truncated(ad.keyword, 160) },
  { id: 'caption', label: 'Caption', group: 'Ad', sortable: true,
    render: (ad) => truncated(ad.caption, 240) },
  { id: 'cta', label: 'CTA', group: 'Ad', sortable: true,
    render: (ad) => ad.cta || '-' },
  { id: 'landingPageUrl', label: 'Landing Page', group: 'Ad',
    render: (ad) => ad.landingPageUrl
      ? <a href={ad.landingPageUrl} target="_blank" rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}>link</a>
      : '-' },
  { id: 'platforms', label: 'Platforms', group: 'Ad', sortable: true,
    render: (ad) => truncated(ad.platforms, 140) },
  { id: 'impressionRank', label: 'Rank', group: 'Ad', align: 'center', sortable: true,
    headerTooltip: "The ad's impression rank within its ad group at the time it was fetched — rank 1 had the most impressions.",
    render: (ad) => ad.impressionRank ?? '-' },
  { id: 'startDate', label: 'Started Running', group: 'Ad', sortable: true,
    render: (ad) => ad.startDate ? dayjs(ad.startDate).format('MMM D, YYYY') : '-' },
  { id: 'scrapedAt', label: 'Fetched', group: 'Ad', sortable: true,
    headerTooltip: 'When this ad was first scraped into the system.',
    render: (ad) => ad.scrapedAt ? dayjs(ad.scrapedAt).format('MMM D, YYYY') : '-' },
  { id: 'updatedAt', label: 'Last Updated', group: 'Ad', sortable: true,
    headerTooltip: "When we last re-fetched this ad (its video URL refreshed). The Fetched Ads tab defaults to ads updated within the last 2 weeks.",
    render: (ad) => ad.updatedAt ? dayjs(ad.updatedAt).format('MMM D, YYYY') : '-' },
  { id: 'advertiserName', label: 'Advertiser', group: 'Ad', sortable: true,
    render: (ad) => truncated(ad.advertiserName, 180) },
  { id: 'productCategory', label: 'Product Category', group: 'Ad', sortable: true,
    render: (ad) => truncated(ad.productCategory, 160) },
  { id: 'tags', label: 'Tags', group: 'Ad',
    render: (ad) => {
      const tags = ad.tags ?? [];
      if (!tags.length) return '-';
      return (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', maxWidth: 220 }}>
          {tags.map(t => <Chip key={t} label={t} size="small" variant="outlined" />)}
        </Box>
      );
    } },
  { id: 'adId', label: 'Ad ID', group: 'Ad',
    render: (ad) => (
      <Tooltip title={ad.adId} arrow>
        <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{ad.adId.slice(0, 8)}</Box>
      </Tooltip>
    ) },

  // ── Pipeline ──
  { id: 'video', label: 'Video', group: 'Pipeline', align: 'center', sortable: true, sortKey: 'videoUrl',
    render: (ad) => ad.videoUrl
      ? <Chip label="Yes" size="small" color="info" />
      : <Chip label="No" size="small" /> },
  { id: 'transcriptionStatus', label: 'Transcription', group: 'Pipeline', align: 'center', sortable: true,
    render: (ad) => statusChip(ad.transcriptionStatus) },
  { id: 'extractionStatus', label: 'Extraction', group: 'Pipeline', align: 'center', sortable: true,
    render: (ad) => statusChip(ad.extractionStatus) },
  { id: 'pipeline', label: 'Pipeline', group: 'Pipeline', align: 'center', sortable: true, sortKey: 'pipeline',
    render: (ad, ctx) => {
      const isProcessing = ctx.processingAdIds.has(ad.adId);
      const t = ad.transcriptionStatus === 'DONE';
      const e = ad.extractionStatus === 'DONE';
      const anyStarted = !!ad.transcriptionStatus || !!ad.extractionStatus;
      const allDone = t && e;
      const anyFailed = ad.transcriptionStatus === 'FAILED' || ad.extractionStatus === 'FAILED';
      // Consistent capitalization across all three lines — raw enums come
      // back UPPER from Snowflake; title-case them so the tooltip reads
      // like a status board, not a SQL dump.
      const fmt = (s: string | null | undefined): string => {
        if (!s) return 'Not started';
        const v = String(s).toLowerCase();
        return v.charAt(0).toUpperCase() + v.slice(1);
      };
      const analysis = t && !e ? 'Pending' : e ? 'Done' : 'Not started';
      const tooltip = `Transcription: ${fmt(ad.transcriptionStatus)}\n`
        + `Analysis: ${analysis}\n`
        + `Extraction: ${fmt(ad.extractionStatus)}`;
      const label = isProcessing ? 'Processing' : allDone ? 'Done' : anyFailed ? 'Failed' : !anyStarted ? 'None' : 'Partial';
      const color = isProcessing ? 'info' : allDone ? 'success' : anyFailed ? 'error' : !anyStarted ? 'default' : 'warning';
      return (
        <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tooltip}</span>} arrow>
          <Chip label={label} size="small" color={color as any}
            icon={isProcessing ? <CircularProgress size={12} color="inherit" /> : undefined} />
        </Tooltip>
      );
    } },
  { id: 'overallGrade', label: 'Grade', group: 'Pipeline', align: 'center', sortable: true,
    render: (ad) => ad.overallGrade ?? '-' },
  { id: 'videoLengthSec', label: 'Video Length', group: 'Pipeline', align: 'center', sortable: true,
    render: (ad) => ad.videoLengthSec != null ? `${ad.videoLengthSec}s` : '-' },

  // ── Video Creative — each shows a prevalence breakdown on hover ──
  featureColumn('awarenessLevel', 'Awareness'),
  featureColumn('hookDevice', 'Hook Device'),
  featureColumn('persuasionAngle', 'Persuasion Angle'),
  featureColumn('proofType', 'Proof Type'),
  featureColumn('narrativeStructure', 'Narrative Structure'),
  featureColumn('primaryEmotion', 'Emotion'),
  featureColumn('presenter', 'Presenter'),
  featureColumn('productionFormat', 'Production Format'),
  featureColumn('tone', 'Tone'),
  featureColumn('offerType', 'Offer Type'),
  featureColumn('ctaType', 'CTA Type'),
  { id: 'segmentCount', label: 'Segments', group: 'Video Creative', align: 'center', sortable: true,
    render: (ad) => ad.segmentCount ?? '-' },
  { id: 'scriptsCount', label: 'Scripts', group: 'Video Creative', align: 'center', sortable: true,
    headerTooltip: 'Number of generated scripts that use this ad as a template. Click to open the Scripts tab filtered to this ad.',
    render: (ad, ctx) => {
      const count = ctx.templateUsage?.get(ad.adId) ?? 0;
      if (count === 0) {
        return <Box component="span" sx={{ color: 'text.disabled' }}>0</Box>;
      }
      return (
        <Chip
          label={count}
          size="small"
          color="primary"
          variant="outlined"
          clickable
          onClick={(e) => {
            e.stopPropagation();
            ctx.onScriptsClick?.(ad.adId);
          }}
          sx={{ height: 22, minWidth: 36, cursor: 'pointer' }}
        />
      );
    } },
];

export const COLUMN_BY_ID: Record<string, ColumnDef> =
  Object.fromEntries(ALL_COLUMNS.map(c => [c.id, c]));

export const COLUMN_GROUPS: ColumnGroup[] = ['Ad', 'Pipeline', 'Video Creative'];

/** Built-in (non-deletable) presets. */
export const BUILTIN_PRESETS: { name: string; columnIds: string[] }[] = [
  {
    name: 'Default',
    columnIds: [
      'pageName', 'pipeline', 'scriptsCount', 'keyword', 'impressionRank', 'startDate', 'updatedAt', 'hook', 'hookDevice',
      'narrativeStructure', 'proofType', 'presenter', 'awarenessLevel',
      'persuasionAngle', 'primaryEmotion', 'tags', 'landingPageUrl',
    ],
  },
  {
    name: 'Pipeline',
    columnIds: ['pageName', 'headline', 'keyword', 'impressionRank', 'video', 'pipeline', 'startDate', 'scrapedAt', 'updatedAt'],
  },
];
